/**
 * Marketing attribution capture (client-side, first-touch).
 *
 * Captures the acquisition evidence that exists ONLY in the browser at landing time —
 * UTM params plus ad-platform click ids (gclid/fbclid/msclkid/li_fat_id) — and latches
 * it into localStorage so it survives the full-page OAuth redirects and can be attached
 * to signup events. First-touch is never overwritten; last-touch is refreshed.
 *
 * This is the single source of truth for attribution on the web app. Landing pages call
 * `captureLandingAttribution()` on mount; signup sites read `getAttribution()`.
 *
 * NOTE: purely client-side. No backend/DB write here — that's a separate (Stripe/CAPI)
 * concern deliberately out of scope for this change.
 */

const FIRST_TOUCH_KEY = 'numeo_attribution_first'
const LAST_TOUCH_KEY = 'numeo_attribution_last'

/** UTM params + ad-platform click ids we persist for attribution. */
export interface Attribution {
    utm_source?: string
    utm_medium?: string
    utm_campaign?: string
    utm_term?: string
    utm_content?: string
    gclid?: string // Google Ads
    gbraid?: string // Google Ads, web-to-app on iOS
    wbraid?: string // Google Ads, app-to-web on iOS
    dclid?: string // Google Display & Video
    fbclid?: string // Meta
    msclkid?: string // Microsoft Ads
    li_fat_id?: string // LinkedIn
    ttclid?: string // TikTok
    twclid?: string // X / Twitter
    rdt_cid?: string // Reddit
    ScCid?: string // Snapchat
    epik?: string // Pinterest
    yclid?: string // Yandex
    tgclid?: string // Telegram Ads
    ko_click_id?: string // Kochava
    /**
     * Name of a click-id-shaped param we do not have a typed field for — see CLICK_ID_PATTERN.
     * Stored so a platform we have never heard of surfaces as itself instead of as `(none)`.
     */
    click_id_param?: string
    fbp?: string // Meta browser cookie (_fbp) — for CAPI Event Match Quality
    fbc?: string // Meta click cookie (_fbc) — for CAPI Event Match Quality
    referrer?: string
    landing_page?: string
}

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const
/**
 * Every click id we capture into a typed field. Matches the set Amplitude's own SDK captures,
 * which is the de-facto industry list.
 *
 * Exported because `channel.ts` must name a channel for EVERY key here — it types its map as
 * `Record<ClickIdKey, string>`, so adding a key below without naming it there is a compile error
 * rather than a click that silently reports as organic.
 */
export const CLICK_ID_KEYS = [
    'gclid',
    'gbraid',
    'wbraid',
    'dclid',
    'fbclid',
    'msclkid',
    'li_fat_id',
    'ttclid',
    'twclid',
    'rdt_cid',
    'ScCid',
    'epik',
    'yclid',
    'tgclid',
    'ko_click_id',
] as const

/** A click id we capture into a typed field. */
export type ClickIdKey = (typeof CLICK_ID_KEYS)[number]

/**
 * Catches a click id from a platform not in the list above, so a new ad network shows up as
 * itself rather than disappearing into `(none)` until someone notices and adds it.
 *
 * Deliberately narrow — matches the naming convention click ids actually follow (`*clid`,
 * `*_cid`, `*click_id`) rather than sweeping every query param, which would turn pagination and
 * session junk into invented channels. `cid` alone is excluded: too many unrelated uses.
 */
const CLICK_ID_PATTERN = /(?:clid|_cid|click_?id)$/i
// Meta cookies set by the pixel. Not URL params — read from document.cookie. Stored under
// keys without the leading underscore (_fbp -> fbp) so they're clean event-property names.
const META_COOKIE_MAP = [
    { cookie: '_fbp', key: 'fbp' },
    { cookie: '_fbc', key: 'fbc' },
] as const

/** Reads a cookie value by name. Empty string if absent / SSR. */
const readCookie = (name: string): string => {
    if (typeof document === 'undefined') return ''
    const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`))
    return m ? decodeURIComponent(m[1]) : ''
}

const read = (key: string): Attribution => {
    try {
        const raw = localStorage.getItem(key)
        return raw ? (JSON.parse(raw) as Attribution) : {}
    } catch {
        return {}
    }
}

const write = (key: string, value: Attribution): void => {
    try {
        localStorage.setItem(key, JSON.stringify(value))
    } catch {
        /* storage full / disabled — attribution is best-effort */
    }
}

/** Extracts UTM + click ids from the URL and Meta cookies from document.cookie. Omits empty values. */
const readFromUrl = (search: string): Attribution => {
    const params = new URLSearchParams(search)
    const out: Attribution = {}
    for (const k of UTM_KEYS) {
        const v = params.get(k)
        if (v) out[k] = v
    }
    for (const k of CLICK_ID_KEYS) {
        const v = params.get(k)
        if (v) out[k] = v
    }
    // A click id from a platform we cannot name. Only its NAME is kept — the value identifies a
    // single click and is useless to group by, while the name is the channel. First match wins;
    // a visitor arrives from one ad, not several.
    if (!CLICK_ID_KEYS.some((k) => out[k])) {
        const unknown = [...params.keys()].find((k) => CLICK_ID_PATTERN.test(k) && params.get(k))
        if (unknown) out.click_id_param = unknown
    }
    // Meta cookies (_fbp/_fbc) — read from the cookie jar, or accept a forwarded copy
    // from the landing-page passthrough if the pixel hasn't set the cookie yet.
    for (const { cookie, key } of META_COOKIE_MAP) {
        const v = readCookie(cookie) || params.get(cookie) || ''
        if (v) out[key] = v
    }
    return out
}

const hasAny = (a: Attribution): boolean => Object.keys(a).length > 0

// Real acquisition evidence = a UTM or a click id. The Meta cookies (_fbp/_fbc) are
// present on almost every page once the pixel runs, so they must NOT by themselves
// trigger a capture — otherwise first-touch would latch for organic visitors with no
// campaign. They ride along as enrichment only when genuine evidence is present.
// `click_id_param` counts: a click id we cannot name is still a click id, and leaving it out
// would drop the very arrivals the unknown-platform sweep exists to catch.
const EVIDENCE_KEYS: (keyof Attribution)[] = [...UTM_KEYS, ...CLICK_ID_KEYS, 'click_id_param']
const hasEvidence = (a: Attribution): boolean => EVIDENCE_KEYS.some((k) => a[k])

/**
 * Captures attribution from the current landing URL. Call once on landing-page mount.
 * - First-touch: stored once, NEVER overwritten (the original acquisition source).
 * - Last-touch: refreshed on every landing that carries params.
 * Adds referrer + landing page for context. No-ops when the URL carries no UTM/click-id
 * evidence (the Meta cookies alone don't count — they'd be present for everyone).
 */
export const captureLandingAttribution = (): void => {
    if (typeof window === 'undefined') return
    const fromUrl = readFromUrl(window.location.search)
    if (!hasEvidence(fromUrl)) return

    const enriched: Attribution = {
        ...fromUrl,
        referrer: document.referrer || undefined,
        landing_page: window.location.pathname || undefined,
    }

    // First-touch: only set if nothing captured before.
    const first = read(FIRST_TOUCH_KEY)
    if (!hasAny(first)) write(FIRST_TOUCH_KEY, enriched)

    // Last-touch: always refresh.
    write(LAST_TOUCH_KEY, enriched)
}

/**
 * Returns the attribution to attach to a signup — first-touch is authoritative for
 * acquisition; last-touch fills any gaps. Returns an object safe to spread into event props.
 */
export const getAttribution = (): Attribution => {
    const first = read(FIRST_TOUCH_KEY)
    const last = read(LAST_TOUCH_KEY)
    return { ...last, ...first } // first-touch wins on conflict
}
